import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import VideoPlayer from '../components/VideoPlayer';
import './CourseDetail.css';

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [viewingMaterial, setViewingMaterial] = useState(null);
  const [unlockStatus, setUnlockStatus] = useState({ modules: [], lessons: [] });

  // Check if user is admin or instructor (they don't enroll, they manage)
  const isAdminOrInstructor = user?.role === 'admin' || user?.role === 'sub-admin' || user?.role === 'instructor';
  const canManageCourse = isAdminOrInstructor && (user?.role === 'admin' || user?.role === 'sub-admin' || course?.instructor_id === user?.id);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const courseRes = await api.get(`/courses/${id}`);
        setCourse(courseRes.data);

        if (user) {
          // Check if enrolled
          const enrollmentRes = await api.get('/enrollments/my-courses');
          const enrollment = enrollmentRes.data.find(
            (e) => e.course_id === parseInt(id)
          );
          const isEnrolled = !!enrollment;
          setEnrolled(isEnrolled);

          // Check if course is completed
          if (enrollment && enrollment.completed_at) {
            setCourseCompleted(true);
            // Fetch certificate if exists
            try {
              const certRes = await api.get('/certificates/my-certificates');
              const courseCert = certRes.data.find(c => c.course_id === parseInt(id));
              if (courseCert) {
                setCertificate(courseCert);
              }
            } catch (certError) {
              console.error('Error fetching certificate:', certError);
            }
          }

          // Get progress if enrolled
          if (isEnrolled) {
            const progressRes = await api.get(`/progress/course/${id}`);
            setProgress(progressRes.data);

            // Fetch module unlock statuses for drip content
            try {
              const unlockRes = await api.get(`/scheduling/unlocked/${id}`);
              setUnlockStatus(unlockRes.data);
            } catch (unlockErr) {
              console.error('Error fetching unlock status:', unlockErr);
            }
          }

          // Fetch quizzes and assignments for enrolled users or instructors
          if (isEnrolled || user.role === 'instructor' || user.role === 'admin' || user.role === 'sub-admin') {
            try {
              const quizzesRes = await api.get(`/quizzes/course/${id}`);
              setQuizzes(quizzesRes.data || []);
            } catch (quizError) {
              console.error('Error fetching quizzes:', quizError);
            }

            try {
              const assignmentsRes = await api.get(`/assignments/course/${id}`);
              setAssignments(assignmentsRes.data || []);
            } catch (assignmentError) {
              console.error('Error fetching assignments:', assignmentError);
            }
          }

          // Fetch course materials for enrolled users and instructors
          if (isEnrolled || isAdminOrInstructor) {
            try {
              const materialsRes = await api.get(`/courses/${id}/materials`);
              setMaterials(materialsRes.data || []);
            } catch (materialsError) {
              console.error('Error fetching course materials:', materialsError);
            }
          }

          // Fetch enrolled students if admin or instructor
          if (canManageCourse) {
            try {
              const studentsRes = await api.get(`/courses/${id}/students`);
              setCourseStudents(studentsRes.data || []);
            } catch (studentsError) {
              console.error('Error fetching course students:', studentsError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id, user]);

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (course?.requires_subscription) {
      navigate('/pricing');
      return;
    }

    const price = course?.sale_price || course?.price;
    if (price && parseFloat(price) > 0 && !course?.is_free) {
      navigate(`/checkout/${id}`);
      return;
    }

    try {
      await api.post('/enrollments', { course_id: id });
      setEnrolled(true);
      window.location.reload();
    } catch (error) {
      if (error.response?.data?.requires_subscription) {
        navigate('/pricing');
        return;
      }
      console.error('Error enrolling:', error);
      alert(t('courseDetail.enrollFailed'));
    }
  };

  const handleVideoComplete = async (videoId) => {
    try {
      await api.post(`/progress/video/${videoId}`, {
        completed: true,
        watch_time: 0,
      });

      // Update progress locally
      const updatedProgress = progress.map((p) =>
        p.video_id === videoId ? { ...p, completed: true } : p
      );
      setProgress(updatedProgress);

      // Check if all videos are now completed
      const allCompleted = updatedProgress.every((p) => p.completed);
      if (allCompleted && course?.videos?.length > 0) {
        // Auto-complete the course
        try {
          await api.post(`/enrollments/${id}/complete`);
          setCourseCompleted(true);
        } catch (completeError) {
          console.error('Error completing course:', completeError);
        }
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleGenerateCertificate = async () => {
    setGeneratingCertificate(true);
    try {
      const res = await api.post(`/certificates/generate/${id}`);
      setCertificate(res.data);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert(t('courseDetail.certFailed'));
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const response = await api.get(`/certificates/download/${certificate.id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${certificate.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert(t('courseDetail.downloadFailed'));
    }
  };

  const handleViewMaterial = async (material) => {
    try {
      const res = await api.get(`/courses/${id}/materials/${material.id}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=1';
      setViewingMaterial({ title: material.title, blobUrl });
    } catch (err) {
      alert(t('courseDetail.pdfFailed'));
    }
  };

  const modalCloseRef = useRef(null);
  const modalPreviousFocusRef = useRef(null);

  const closeViewer = useCallback(() => {
    if (viewingMaterial?.blobUrl) {
      URL.revokeObjectURL(viewingMaterial.blobUrl);
    }
    setViewingMaterial(null);
    modalPreviousFocusRef.current?.focus();
  }, [viewingMaterial]);

  useEffect(() => {
    if (viewingMaterial) {
      modalPreviousFocusRef.current = document.activeElement;
      modalCloseRef.current?.focus();
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') closeViewer();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [viewingMaterial, closeViewer]);

  // Format unlock countdown for drip content
  const formatUnlockDate = (unlocksAt) => {
    if (!unlocksAt) return null;
    const now = new Date();
    const unlock = new Date(unlocksAt);
    const diffMs = unlock - now;
    if (diffMs <= 0) return null;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return t('courseDetail.unlockTomorrow');
    if (diffDays < 7) return t('courseDetail.unlockInDays', { n: diffDays });
    return t('courseDetail.unlockOn', { date: unlock.toLocaleDateString() });
  };

  // Check if a video is unlocked (sequential unlocking)
  const isVideoUnlocked = (video, index) => {
    if (!enrolled) return false;
    if (index === 0) return true; // First video is always unlocked

    // Check if all previous videos are completed
    const sortedVideos = [...(course?.videos || [])].sort(
      (a, b) => a.order_index - b.order_index
    );

    for (let i = 0; i < index; i++) {
      const prevVideo = sortedVideos[i];
      const prevProgress = progress.find((p) => p.video_id === prevVideo.id);
      if (!prevProgress?.completed) {
        return false;
      }
    }
    return true;
  };

  // Calculate progress percentage
  const completedVideos = progress.filter((p) => p.completed).length;
  const totalVideos = course?.videos?.length || 0;
  const progressPercentage = totalVideos > 0 ? Math.round((completedVideos / totalVideos) * 100) : 0;

  if (loading) {
    return <div className="loading">{t('courseDetail.loading')}</div>;
  }

  if (!course) {
    return <div className="page">{t('courseDetail.notFound')}</div>;
  }

  const videoProgress = progress.find((p) => p.video_id === selectedVideo?.id);

  return (
    <div className="page">
      <div className="course-detail-container">
        <div className="course-header">
          <h1>{course.title}</h1>
          <p className="course-category">{course.category_name}</p>
          <p className="course-instructor">
            {t('courseDetail.instructorLabel')}: {course.instructor_name}
          </p>
          <p className="course-description">{course.description}</p>

          {/* Admin/Instructor view - Edit button instead of enroll */}
          {isAdminOrInstructor ? (
            <div className="admin-actions">
              <div className="admin-badge">
                {user?.role === 'admin' || user?.role === 'sub-admin'
                  ? t('courseDetail.adminView')
                  : t('courseDetail.instructorView')}
              </div>
              <button
                onClick={() => navigate(`/edit-course/${id}`)}
                className="btn-primary edit-course-btn"
              >
                {t('courseDetail.editCourse')}
              </button>
            </div>
          ) : (
            <>
              {!enrolled ? (
                <>
                  {course?.requires_subscription && (
                    <span className="subscription-badge" aria-label={t('courseDetail.subscriptionRequired')}>
                      {t('courseDetail.subscriptionRequired')}
                    </span>
                  )}
                  <button onClick={handleEnroll} className="btn-primary enroll-btn">
                    {course?.requires_subscription
                      ? t('courseDetail.subscribeToAccess')
                      : t('courseDetail.enrollInCourse')}
                  </button>
                </>
              ) : (
                <div className="enrollment-status">
                  <div className="enrolled-badge">{t('courseDetail.enrolled')}</div>
                  {totalVideos > 0 && (
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {t('courseDetail.lessonsCompleted', {
                          completed: completedVideos,
                          total: totalVideos,
                          pct: progressPercentage,
                        })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {courseCompleted && (
                <div className="certificate-section">
                  <div className="course-completed-badge">{t('courseDetail.courseCompleted')}</div>
                  {certificate ? (
                    <div className="certificate-info">
                      <p>{t('courseDetail.certNumber')}: {certificate.certificate_number}</p>
                      <button
                        onClick={handleDownloadCertificate}
                        className="btn-primary download-cert-btn"
                      >
                        {t('courseDetail.downloadCert')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateCertificate}
                      className="btn-primary generate-cert-btn"
                      disabled={generatingCertificate}
                    >
                      {generatingCertificate
                        ? t('courseDetail.generating')
                        : t('courseDetail.generateCert')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {selectedVideo && (
          <div className="video-player-section">
            <VideoPlayer
              video={selectedVideo}
              onComplete={() => handleVideoComplete(selectedVideo.id)}
              isCompleted={videoProgress?.completed}
            />
          </div>
        )}

        <div className="videos-section">
          <h2>{t('courseDetail.courseContent')}</h2>
          {course.videos && course.videos.length > 0 ? (
            <div className="video-list">
              {[...course.videos]
                .sort((a, b) => a.order_index - b.order_index)
                .map((video, index) => {
                  const vidProgress = progress.find((p) => p.video_id === video.id);
                  const unlocked = isVideoUnlocked(video, index);
                  // Admin/Instructor can always click, students need to be enrolled and have video unlocked
                  const canClick = isAdminOrInstructor || (enrolled && unlocked);

                  return (
                    <div
                      key={video.id}
                      className={`video-item ${selectedVideo?.id === video.id ? 'active' : ''
                        } ${!unlocked && enrolled && !isAdminOrInstructor ? 'locked' : ''}`}
                      onClick={() => canClick && setSelectedVideo(video)}
                      style={{ cursor: canClick ? 'pointer' : 'not-allowed' }}
                    >
                      <div className={`video-number ${vidProgress?.completed ? 'completed' : ''}`}>
                        {vidProgress?.completed ? '✓' : index + 1}
                      </div>
                      <div className="video-info">
                        <h4>{video.title}</h4>
                        <p>{video.description}</p>
                        {video.duration && (
                          <span className="video-duration">
                            {Math.floor(video.duration / 60)} {t('courseDetail.minLabel')}
                          </span>
                        )}
                        {!unlocked && enrolled && !isAdminOrInstructor && (
                          <span className="unlock-hint">{t('courseDetail.unlockPrevious')}</span>
                        )}
                      </div>
                      {!enrolled && !isAdminOrInstructor && <div className="locked-icon">🔒</div>}
                      {enrolled && !unlocked && !isAdminOrInstructor && <div className="locked-icon">🔒</div>}
                    </div>
                  );
                })}
            </div>
          ) : (
            <p>{t('courseDetail.noVideos')}</p>
          )}
        </div>

        {/* Drip Content — Module Unlock Status (enrolled students only) */}
        {enrolled && !isAdminOrInstructor && unlockStatus.modules.length > 0 && (
          <div className="drip-status-section">
            <h2>{t('courseDetail.courseModules')}</h2>
            <div className="drip-modules-list">
              {unlockStatus.modules.map((mod) => {
                const countdown = !mod.unlocked ? formatUnlockDate(mod.unlocks_at) : null;
                return (
                  <div
                    key={mod.id}
                    className={`drip-module-item ${mod.unlocked ? 'drip-unlocked' : 'drip-locked'}`}
                  >
                    <div className="drip-module-icon">
                      {mod.unlocked ? '✓' : '🔒'}
                    </div>
                    <div className="drip-module-info">
                      <span className="drip-module-title">{mod.title}</span>
                      {!mod.unlocked && countdown && (
                        <span className="drip-countdown">{countdown}</span>
                      )}
                      {!mod.unlocked && !countdown && mod.unlocks_at && (
                        <span className="drip-countdown">
                          {t('courseDetail.unlockOn', { date: new Date(mod.unlocks_at).toLocaleDateString() })}
                        </span>
                      )}
                      {mod.is_preview && (
                        <span className="drip-preview-badge">{t('courseDetail.freePreview')}</span>
                      )}
                    </div>
                    {mod.unlocked && (
                      <span className="drip-available-badge">{t('courseDetail.available')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Course Materials (PDF) Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && materials.length > 0 && (
          <div className="materials-section">
            <h2>{t('courseDetail.courseMaterials')}</h2>
            <div className="materials-list">
              {materials.map((material) => (
                <div key={material.id} className="material-item">
                  <div className="material-icon">
                    <span>PDF</span>
                  </div>
                  <div className="material-info">
                    <h4 className="material-title">{material.title}</h4>
                    <p className="material-meta">
                      {material.file_name}
                      {material.file_size && ` · ${(material.file_size / 1024).toFixed(0)} KB`}
                    </p>
                  </div>
                  <button
                    className="btn-primary view-pdf-btn"
                    onClick={() => handleViewMaterial(material)}
                  >
                    {t('courseDetail.viewPdf')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PDF Viewer Modal */}
        {viewingMaterial && (
          <div
            className="pdf-viewer-overlay"
            onClick={closeViewer}
            role="dialog"
            aria-modal="true"
            aria-label={viewingMaterial.title}
          >
            <div className="pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pdf-viewer-header">
                <h3>{viewingMaterial.title}</h3>
                <button
                  ref={modalCloseRef}
                  type="button"
                  className="modal-close"
                  onClick={closeViewer}
                  aria-label="Close PDF viewer"
                >
                  &times;
                </button>
              </div>
              <div className="pdf-viewer-body">
                <iframe
                  src={viewingMaterial.blobUrl}
                  title={viewingMaterial.title}
                  className="pdf-iframe"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            </div>
          </div>
        )}

        {/* Quizzes Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="quizzes-section">
            <div className="section-header">
              <h2>{t('courseDetail.quizzesSection')}</h2>
              {canManageCourse && (
                <button
                  className="btn-primary add-btn"
                  onClick={() => navigate(`/courses/${id}/quiz/new`)}
                >
                  {t('courseDetail.addQuiz')}
                </button>
              )}
            </div>
            {quizzes.length > 0 ? (
              <div className="assessment-grid">
                {quizzes.map((quiz) => (
                  <div key={quiz.id} className="assessment-card quiz-card">
                    <div className="assessment-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div className="assessment-info">
                      <h4>{quiz.title}</h4>
                      {quiz.description && <p className="assessment-desc">{quiz.description}</p>}
                      <div className="assessment-meta">
                        <span className="meta-item">
                          {t('courseDetail.questionsCount', { count: quiz.question_count || 0 })}
                        </span>
                        <span className="meta-item">
                          {t('courseDetail.passLabel')}: {quiz.passing_score || 70}%
                        </span>
                        {quiz.time_limit > 0 && (
                          <span className="meta-item">
                            {Math.floor(quiz.time_limit / 60)} {t('courseDetail.minLabel')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="assessment-actions">
                      <button
                        className="btn-primary take-btn"
                        onClick={() => navigate(`/quiz/${quiz.id}`)}
                      >
                        {canManageCourse ? t('courseDetail.preview') : t('courseDetail.takeQuiz')}
                      </button>
                      {canManageCourse && (
                        <button
                          className="btn-secondary edit-btn"
                          onClick={() => navigate(`/quiz/${quiz.id}/manage`)}
                        >
                          {t('courseDetail.editBtn')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-assessment">
                <p>{t('courseDetail.noQuizzes')}</p>
              </div>
            )}
          </div>
        )}

        {/* Assignments Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="assignments-section">
            <div className="section-header">
              <h2>{t('courseDetail.assignmentsSection')}</h2>
              {canManageCourse && (
                <button
                  className="btn-primary add-btn"
                  onClick={() => navigate(`/courses/${id}/assignments`)}
                >
                  {t('courseDetail.manageAssignments')}
                </button>
              )}
            </div>
            {assignments.length > 0 ? (
              <div className="assessment-grid">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="assessment-card assignment-card">
                    <div className="assessment-icon assignment-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="assessment-info">
                      <h4>{assignment.title}</h4>
                      {assignment.description && <p className="assessment-desc">{assignment.description}</p>}
                      <div className="assessment-meta">
                        <span className="meta-item">
                          {t('courseDetail.maxScore')}: {assignment.max_score || 100}
                        </span>
                        {assignment.due_date && (
                          <span className="meta-item due-date">
                            {t('courseDetail.due')}: {new Date(assignment.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="assessment-actions">
                      {!canManageCourse ? (
                        <button
                          className="btn-primary submit-btn"
                          onClick={() => navigate(`/assignment/${assignment.id}/submit`)}
                        >
                          {t('courseDetail.submitAssignment')}
                        </button>
                      ) : (
                        <button
                          className="btn-primary view-btn"
                          onClick={() => navigate(`/assignment/${assignment.id}/submissions`)}
                        >
                          {t('courseDetail.viewSubmissions')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-assessment">
                <p>{t('courseDetail.noAssignments')}</p>
              </div>
            )}
          </div>
        )}

        {/* Community Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="community-section">
            <div className="section-header">
              <h2>{t('courseDetail.community')}</h2>
            </div>
            <div className="community-grid">
              <Link to={`/courses/${id}/announcements`} className="community-card announcements-card">
                <div className="community-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div className="community-info">
                  <h4>{t('courseDetail.announcementsCard')}</h4>
                  <p>{t('courseDetail.announcementsCardDesc')}</p>
                </div>
                <div className="community-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </Link>

              <Link to={`/courses/${id}/questions`} className="community-card questions-card">
                <div className="community-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <path d="M8 9h8M8 13h6" />
                  </svg>
                </div>
                <div className="community-info">
                  <h4>{t('courseDetail.qaForum')}</h4>
                  <p>{t('courseDetail.qaForumDesc')}</p>
                </div>
                <div className="community-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </Link>

              {canManageCourse && (
                <Link to={`/courses/${id}/manage-announcements`} className="community-card manage-card">
                  <div className="community-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </div>
                  <div className="community-info">
                    <h4>{t('courseDetail.manageAnnouncementsCard')}</h4>
                    <p>{t('courseDetail.manageAnnouncementsCardDesc')}</p>
                  </div>
                  <div className="community-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </Link>
              )}

              {canManageCourse && (
                <Link to={`/courses/${id}/drip-schedule`} className="community-card drip-card">
                  <div className="community-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="community-info">
                    <h4>{t('courseDetail.dripScheduleCard')}</h4>
                    <p>{t('courseDetail.dripScheduleCardDesc')}</p>
                  </div>
                  <div className="community-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Enrolled Students Section - Admin/Instructor Only */}
        {canManageCourse && (
          <div className="enrolled-students-section">
            <div className="section-header">
              <h2>{t('courseDetail.enrolledStudents')}</h2>
              <span className="student-count-badge">
                {courseStudents.length}{' '}
                {courseStudents.length === 1
                  ? t('courseDetail.studentBadge')
                  : t('courseDetail.studentsBadge')}
              </span>
            </div>

            {courseStudents.length > 0 ? (
              <div className="students-table-container">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>{t('courseDetail.studentCol')}</th>
                      <th>{t('courseDetail.enrolledDateCol')}</th>
                      <th>{t('courseDetail.progressCol')}</th>
                      <th>{t('courseDetail.quizAvgCol')}</th>
                      <th>{t('courseDetail.lessonsCol')}</th>
                      <th>{t('courseDetail.statusCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseStudents.map((student) => (
                      <tr key={student.user_id}>
                        <td>
                          <div className="student-info-cell">
                            <div className="student-avatar">
                              {student.avatar_url ? (
                                <img src={student.avatar_url} alt={student.first_name} />
                              ) : (
                                <div className="avatar-placeholder">
                                  {student.first_name?.[0] || student.email[0].toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="student-details">
                              <strong>{student.first_name} {student.last_name}</strong>
                              <span className="student-email">{student.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{new Date(student.enrolled_at).toLocaleDateString()}</td>
                        <td>
                          <div className="progress-cell">
                            <div className="progress-bar small">
                              <div
                                className="progress-fill"
                                style={{ width: `${student.progress_percentage || 0}%` }}
                              />
                            </div>
                            <span className="progress-text-small">{Math.round(student.progress_percentage || 0)}%</span>
                          </div>
                        </td>
                        <td>{Math.round(student.avg_quiz_score || 0)}%</td>
                        <td>{student.lessons_completed}/{student.total_lessons}</td>
                        <td>
                          <span className={`status-badge ${student.completed_at ? 'completed' : 'in-progress'}`}>
                            {student.completed_at
                              ? t('courseDetail.completedStatus')
                              : t('courseDetail.activeStatus')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <h3>{t('courseDetail.noStudents')}</h3>
                <p>{t('courseDetail.noStudentsDesc')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
